import { useState } from "react";
import PaymentForm from "./PaymentForm";
import OrderSummary from "./OrderSummary";
import { Order, OwnerType } from "@/api/SolarCloudApi";
import LoadingSection from "@/components/LoadingSection";
import { useCompleteStep } from "../hook";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { useOrderUuid } from "@/stores/orderIdStore";
import { useUser } from "@/queries/user/useUser";
import { Navigate, useNavigate } from "react-router";
import { useBuyPanelsStore } from "../store";
import BackButton from "@/components/ui/BackButton";
import { useQueryClient } from "@tanstack/react-query";
import getTrackerEventsQueryOptions from "@/queries/tracker/useTrackerEvents";
import getUsersOrdersQueryOptions from "@/queries/order/useUsersOrders";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { usePayPalClientToken, useEnvironmentConfig } from "@/queries/environment/useEnvironmentConfig";
import { getBuyPanelsPath } from "../route";

type PaymentProps = {
  disableRedirect?: boolean;
  fullWidth?: boolean;
  allowPayment?: boolean;
  requiresPaymentDetails?: boolean;
  orderUuid?: string;
  order?: Order;
  summaryLabel?: string;
  summaryAmount?: number;
  onSuccess?: (order: Order) => void;
};

export default function Payment({
  disableRedirect = false,
  fullWidth = false,
  allowPayment,
  requiresPaymentDetails = false,
  orderUuid: explicitOrderUuid,
  order: orderOverride,
  summaryLabel,
  summaryAmount,
  onSuccess: onSuccessOverride,
}: PaymentProps) {
  const queryClient = useQueryClient();
  const [isPaying, setIsPaying] = useState(false);
  const completeStep = useCompleteStep();
  const storeOrderUuid = useOrderUuid();
  const orderUuid = explicitOrderUuid ?? storeOrderUuid;
  const { data: user, isLoading: isUserLoading } = useUser();
  const { data: fetchedOrder, isLoading: isOrderLoading } = useNewOrder({
    uuid: orderOverride ? undefined : orderUuid,
    queryConfig: {
      enabled: !orderOverride,
    },
  });
  const order = orderOverride ?? fetchedOrder;
  const getPreviousStep = useBuyPanelsStore((state) => state.getPreviousStep);
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(getPreviousStep());
  };

  const onSuccess = (order: Order) => {
    queryClient.invalidateQueries({
      queryKey: getTrackerEventsQueryOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: getUsersOrdersQueryOptions().queryKey,
    });
    if (onSuccessOverride) {
      onSuccessOverride(order);
      return;
    }

    completeStep(`order=${order.uuid}`);
  };

  const { data: paypalTokenData, isLoading: isPayPalTokenLoading, isError: isPayPalTokenError } = usePayPalClientToken();
  const { data: env } = useEnvironmentConfig();
  const clientId = env?.paypal?.clientId ?? "";
  // Default currency to AUD to match backend orders (update if backend exposes currency)
  const currency = "AUD";
  const showPayPal = !isPayPalTokenError && !!paypalTokenData?.clientToken && !!clientId;
  const resolvedAllowPayment =
    typeof allowPayment !== "undefined"
      ? allowPayment
      : !!(
          order?.owner &&
          ((order.owner.type === OwnerType.USER && order.owner.user?.uuid) ||
            (order.owner.type === OwnerType.ORGANISATION &&
              order.owner.organisation?.uuid))
        );
  const routePrefix = getBuyPanelsPath(window.location.pathname, "");

  if (isUserLoading || isOrderLoading || isPayPalTokenLoading) {
    return <LoadingSection />;
  }

  if (!order && disableRedirect) {
    return (
      <div className="p-4 text-sm text-muted-foreground">Order is loading or not available yet.</div>
    );
  }

  if (isPayPalTokenError || !paypalTokenData?.clientToken) {
    // eslint-disable-next-line no-console
    console.warn("PayPal token could not be fetched; PayPal payments may not be available.");
  }

  if (!disableRedirect) {
    if (!order || order.items?.length === 0) {
      return <Navigate to={routePrefix} />;
    } else if (!order.owner) {
      return <Navigate to={`${routePrefix}/signup`} />;
    } else if (order.owner.type === OwnerType.ORGANISATION) {
      if (!order.owner.organisation) {
        return <Navigate to={`${routePrefix}/ownership`} />;
      }
    } else if (order.owner.type === OwnerType.USER) {
      if (!order.owner.user || !user?.givenName || !user?.familyName) {
        return <Navigate to={`${routePrefix}/ownership`} />;
      }
    }
  }

  return (
    <>
      {/* <BackButton handleBack={handleBack} disabled={isPaying} /> */}
      {showPayPal ? (
        <PayPalScriptProvider
          options={{
            clientId: clientId,
            "client-id": clientId,
            currency: currency,
            "data-client-token": paypalTokenData?.clientToken ?? "",
            components: "buttons",
            intent: "capture",
            "disable-funding": "card",
          }}
        >
          <div className={fullWidth ? "w-full" : "max-w-lg mx-auto"}>
              {order && (
                <>
                  <PaymentForm
                    order={order}
                    setIsPaying={setIsPaying}
                    handlePaymentSuccess={onSuccess}
                    isPaying={isPaying}
                    showPayPal={showPayPal}
                    fullWidth={fullWidth}
                    allowPayment={resolvedAllowPayment}
                    requiresPaymentDetails={requiresPaymentDetails}
                  />
                  <OrderSummary
                    order={order}
                    summaryLabel={summaryLabel}
                    summaryAmount={summaryAmount}
                  />
                </>
              )}
            </div>
        </PayPalScriptProvider>
      ) : (
        <div className={fullWidth ? "w-full" : "max-w-lg mx-auto"}>
          {(isPayPalTokenError || !paypalTokenData?.clientToken || !clientId) && (
            <div className="mb-4 p-4 rounded bg-muted">PayPal is currently unavailable. Please choose another payment method.</div>
          )}
          {order && (
            <>
              <PaymentForm
                order={order}
                setIsPaying={setIsPaying}
                handlePaymentSuccess={onSuccess}
                isPaying={isPaying}
                showPayPal={false}
                fullWidth={fullWidth}
                allowPayment={resolvedAllowPayment}
                requiresPaymentDetails={requiresPaymentDetails}
              />
              <OrderSummary
                order={order}
                summaryLabel={summaryLabel}
                summaryAmount={summaryAmount}
              />
            </>
          )}
        </div>
      )}
      {/* <PaymentOptions /> */}
    </>
  );
}
